import { HubConnectionBuilder, JsonHubProtocol, LogLevel } from "@microsoft/signalr";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sortBy } from "lodash";
import { moduleName, updateNotificationIconBadgeFromNotifications } from "./common";
import { updateNotificationsLastSyncDate } from "./notifications";
import { OperationAction } from "common/types/operationAction";
import { CONNECTION_ID_UPDATED, LAST_SYNC_DATE_UPDATED, OPERATION_UPDATED, publishToMessageBus } from "common/ins-message-bus";
import { NotificationStatus } from "../types";
import { appStore } from "index";
import { getConfig } from "plugins/config-loader";
export let tokenGetter = null;
export let userIdGetter = () => "";
export const defineTokenGetter = (getter, idGetter) => {
    tokenGetter = (state) => getter(state);
    userIdGetter = (state) => idGetter(state);
};
export const convertProtoTimestampToDate = (messageDate) => {
    const NANOS_PER_MILLISECOND = 1e6;
    const milliseconds = (messageDate.seconds * 1000) + (messageDate.nanos / NANOS_PER_MILLISECOND);
    return new Date(milliseconds);
};
export const formatNotificationMessageArray = (data) => data.map((notification) => {
    const correctedNotification = { ...notification };
    correctedNotification.message.messageDate = convertProtoTimestampToDate(notification?.message?.messageDate);
    return correctedNotification;
});
export const sortNotifications = (notificationArray) => sortBy(notificationArray, (notif) => {
    if (notif.message && notif.message.messageDate) {
        return -new Date(notif.message.messageDate).getTime();
    }
    return 0;
});
const isDataSyncAction = (action) => action === OperationAction.Push || action === OperationAction.Transform;
// Exported for testing only - do not use outside of notifications module
export const updateLastSyncDateFromNotifications = (notifications) => {
    const completedSyncs = notifications.filter((n) => isDataSyncAction(n.action) && n.status === NotificationStatus.COMPLETED);
    if (!completedSyncs.length) {
        return;
    }
    const latestSyncDate = completedSyncs.reduce((latest, current) => {
        const currentDate = new Date(current.message.messageDate);
        return currentDate > latest ? currentDate : latest;
    }, new Date(completedSyncs[0].message.messageDate));
    const hasSyncInProgress = notifications.some((n) => isDataSyncAction(n.action) && n.status !== NotificationStatus.COMPLETED);
    appStore.dispatch(updateNotificationsLastSyncDate({
        lastSynchronisationDate: latestSyncDate,
        synInProgress: hasSyncInProgress
    }));
    publishToMessageBus(LAST_SYNC_DATE_UPDATED, latestSyncDate);
};
const getAppUrl = () => {
    const config = getConfig("REACT_APP_NOTIFICATION_BACKEND_BASE_URL");
    if (config?.indexOf("http") === 0) {
        return config;
    }
    const url = new URL(config, window.location.origin);
    return url.toString();
};
const prepareConnection = (hub) => {
    const options = {
        accessTokenFactory: () => appStore?.getState()?.auth?.oidc?.user?.access_token,
        withCredentials: false
    };
    const url = new URL(hub, getAppUrl()).toString();
    const connection = new HubConnectionBuilder()
        .withUrl(url, options)
        .withAutomaticReconnect()
        .withHubProtocol(new JsonHubProtocol())
        .configureLogging(LogLevel.None)
        .build();
    return connection;
};
const startSignalRConnection = async (connection, requests, postStartConfigure) => {
    const timeOut = 5000;
    try {
        await connection.start();
        if (connection.connectionId) {
            if (postStartConfigure) {
                postStartConfigure(connection);
            }
        }
    }
    catch {
        setTimeout(() => startSignalRConnection(connection, requests, postStartConfigure), timeOut);
    }
};
const customSignalRQuery = async (url) => {
    const connection = prepareConnection(url);
    try {
        await startSignalRConnection(connection, [url]);
    }
    catch (error) {
        if (connection) {
            await connection.stop();
        }
        return { error };
    }
    return { connection };
};
const conn = {};
const invokeOnReadNotificationAsync = async (operationIds) => {
    if (!conn.connection) {
        return { error: true };
    }
    try {
        await conn.connection.invoke("OnReadNotificationAsync", operationIds, userIdGetter(appStore.getState()));
        return { success: true };
    }
    catch (error) {
        return { error };
    }
};
const api = createApi({
    baseQuery: fetchBaseQuery({
        baseUrl: getConfig("REACT_APP_NOTIFICATION_BACKEND_BASE_URL"),
        prepareHeaders: (headers, { getState }) => {
            if (tokenGetter !== null) {
                const token = tokenGetter(getState());
                if (token) {
                    headers.set("authorization", `Bearer ${token}`);
                }
            }
            return headers;
        }
    }),
    endpoints: (builder) => ({
        GetOperationNotifications: builder.query({
            async onCacheEntryAdded(arg, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                await cacheDataLoaded;
                if (conn.connection) {
                    if (conn.connection.connectionId) {
                        publishToMessageBus(CONNECTION_ID_UPDATED, conn.connection.connectionId);
                    }
                    conn.connection.on("operation", (data) => {
                        publishToMessageBus(OPERATION_UPDATED, { action: data.action,
                            input: data.input,
                            message: data.message,
                            operation: data.id,
                            output: data.output,
                            status: data.status });
                        updateCachedData((draft) => {
                            const notifs = draft ?? [];
                            const index = notifs.findIndex((n) => n.id === data.id);
                            const [notif] = formatNotificationMessageArray([data]);
                            if (index < 0) {
                                notifs.push(notif);
                            }
                            else if (notifs[index].message.messageDate < notif.message.messageDate) {
                                notifs.splice(index, 1, notif);
                            }
                            if (notifs.length > 0) {
                                const sortedNotifs = sortNotifications(notifs);
                                draft.length = 0;
                                draft.push(...sortedNotifs);
                                updateNotificationIconBadgeFromNotifications(sortedNotifs);
                                updateLastSyncDateFromNotifications(sortedNotifs);
                            }
                        });
                    });
                    conn.connection.on("operations", (data) => {
                        updateCachedData((draft) => {
                            const notifs = formatNotificationMessageArray(data);
                            const sortedNotifs = sortNotifications(notifs);
                            draft.length = 0;
                            draft.push(...sortedNotifs);
                            updateNotificationIconBadgeFromNotifications(sortedNotifs);
                            updateLastSyncDateFromNotifications(sortedNotifs);
                        });
                    });
                }
                await cacheEntryRemoved;
            },
            providesTags: ["Notifications"],
            queryFn: async () => {
                const copy = conn;
                /*
                 * The thing above was done to get around an eslint error
                 * A proper way to solve that is to implement a lock mechanism
                 * but the issue we are trying to solve here is bound to disappear with story #1806242
                 */
                if (!copy.connection) {
                    const ws = await customSignalRQuery("operation-notification");
                    copy.connection = ws.connection;
                    ws.connection?.onclose(() => {
                        delete conn.connection;
                    });
                    ws.connection?.onreconnected((newConnectionId) => {
                        if (newConnectionId) {
                            publishToMessageBus(CONNECTION_ID_UPDATED, newConnectionId);
                        }
                    });
                    if (ws.error) {
                        // Return { error: ws.error }
                    }
                }
                return { data: [] };
            }
        }),
        onReadNotificationAsync: builder.mutation({
            queryFn: async ({ operationIds }) => {
                const result = await invokeOnReadNotificationAsync(operationIds);
                return { data: result };
            }
        })
    }),
    reducerPath: `${moduleName}.api`,
    tagTypes: ["Notifications"]
});
export const { middleware: apiMiddleware, reducer: apiReducers, reducerPath: apiReducersPath, useGetOperationNotificationsQuery, useOnReadNotificationAsyncMutation, endpoints } = api;
export const getOperationNotificationConnectionId = () => conn.connection?.connectionId ?? "";
